import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { LuLoaderCircle, LuSearch } from 'react-icons/lu';
import { useShallow } from 'zustand/react/shallow';

import { blastsQueryOptions } from '~api/queries';
import type { Blast } from '~api/services';
import { EmptyState, Loader } from '~components';
import { Button } from '~components/ui/button';
import { Input } from '~components/ui/input';
import { ScrollArea } from '~components/ui/scroll-area';
import { useDebounceCallback } from '~hooks';
import { selectActiveMineCode, useFilterStore } from '~store';
import { BlastItem, BlastListFilters } from '../components';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type BlastListContentProps = {
  blasts: Blast[] | undefined;
  loadingBlasts: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const selectFlatPages = (data: InfiniteData<Blast[]>) => data.pages.flat();

/* -------------------------------------------------------------------------- */
/*                                Sub-components                               */
/* -------------------------------------------------------------------------- */

/**
 * Component for rendering the list of blasts along with loading and pagination states.
 * It displays a loader when blasts are being fetched, the blast items when available, and a "Load More" button for pagination.
 */
const BlastListContent = ({
  blasts,
  loadingBlasts,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: BlastListContentProps) => {
  if (loadingBlasts) {
    return (
      <div className="grid h-full w-full place-items-center">
        <Loader orientation="vertical">Loading Blasts...</Loader>
      </div>
    );
  }

  if (!blasts?.length) return null;

  return (
    <div className="space-y-6">
      <div className="grid auto-rows-fr grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {blasts.map((blast) => (
          <BlastItem key={blast.name} blast={blast} />
        ))}
      </div>
      {hasNextPage && (
        <div className="flex justify-center">
          <Button className="min-w-48" variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage && <LuLoaderCircle className="flex animate-spin" />}
            {isFetchingNextPage ? 'Loading more...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Component for displaying a list of blasts with search and filter capabilities.
 * It uses pagination for efficient data loading and provides user feedback for loading states and empty results.
 */
const BlastList = () => {
  const { searchPattern, setSearchPattern, hasOptimisation, hasDrillDomains, needsAttention, resetFilters } =
    useFilterStore(
      useShallow((state) => ({
        searchPattern: state.query,
        setSearchPattern: state.setQuery,
        hasOptimisation: state.hasOptimisation,
        hasDrillDomains: state.hasDrillDomains,
        needsAttention: state.needsAttention,
        resetFilters: state.resetFilters,
      })),
    );

  const [searchInputValue, setSearchInputValue] = useState<string>(searchPattern);
  const debouncedSetSearch = useDebounceCallback(setSearchPattern, 300);

  const activeMineCode = useFilterStore(selectActiveMineCode);

  const {
    data: blasts,
    isLoading: loadingBlasts,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...blastsQueryOptions({
      query: searchPattern || undefined,
      mine_code: activeMineCode,
      has_optimisations: hasOptimisation ?? undefined,
      has_drill_domains: hasDrillDomains ?? undefined,
      needs_attention: needsAttention ?? undefined,
    }),
    select: selectFlatPages,
  });

  const hasBlasts = blasts && blasts.length > 0;
  const hasActiveFilters =
    !!searchPattern ||
    !!activeMineCode ||
    hasOptimisation !== null ||
    hasDrillDomains !== null ||
    needsAttention !== null;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInputValue(value);
    debouncedSetSearch(value.trim());
  };

  return (
    <div className="mx-auto flex w-full max-w-(--max-width-page) flex-col overflow-hidden">
      <div className="my-6 space-y-2 px-6">
        <h2 className="font-bold text-3xl">Site Blasts</h2>
        <p className="text-base text-muted-foreground">
          View and compare blast optimisation scenarios and check blast design context across all sites
        </p>
      </div>

      <div className="mb-6 px-6">
        <Input
          aria-label="Search blasts"
          placeholder="Search blasts..."
          prefix={<LuSearch aria-hidden />}
          onChange={handleSearchChange}
          value={searchInputValue}
        />
      </div>

      <div className="mb-6 flex flex-wrap gap-4 px-6">
        {/* Filters */}
        <BlastListFilters />
      </div>

      {!loadingBlasts &&
        !hasBlasts &&
        (hasActiveFilters ? (
          <EmptyState
            title="No blasts found"
            description="No blasts match your current filter selection. Try adjusting your filters to see more results."
            onPrimaryAction={() => {
              debouncedSetSearch.cancel();
              resetFilters();
              setSearchInputValue('');
            }}
          />
        ) : (
          <EmptyState title="No blasts available" description="There are no blasts to display at this time." />
        ))}

      <ScrollArea className="min-h-0" viewportClassName="flex grow px-4 pb-6 sm:px-6" type="auto">
        <BlastListContent
          blasts={blasts}
          loadingBlasts={loadingBlasts}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      </ScrollArea>
    </div>
  );
};

export { BlastList };
